import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Cpu,
  FileText,
  Rocket,
  Server,
  Sparkles,
  User,
  X,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { agentsApi, type CreateAgentInput, type Agent } from "@/api/agents";
import { modelsApi } from "@/api/models";
import { onboardingApi } from "@/api/onboarding";
import { queryKeys } from "@/lib/query-keys";
import {
  StepTemplate,
  StepIdentity,
  StepWorkspace,
  StepBrain,
  StepInfra,
  StepReview,
} from "./new-agent/steps";
import { VM_SIZES, findAdapterCard, type Template } from "./new-agent/data";
import {
  Provisioning,
  ProvisioningError,
  AgentAlive,
} from "./new-agent/provisioning";

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

const STEPS: Array<{ id: 1 | 2 | 3 | 4 | 5 | 6; label: string; desc: string; icon: LucideIcon }> = [
  { id: 1, label: "Template", desc: "Start from a preset", icon: Sparkles },
  { id: 2, label: "Identity", desc: "Name, role, manager", icon: User },
  { id: 3, label: "Workspace", desc: "Identity files", icon: FileText },
  { id: 4, label: "Brain", desc: "Model & runtime", icon: Cpu },
  { id: 5, label: "Infrastructure", desc: "Where it runs", icon: Server },
  { id: 6, label: "Review", desc: "Deploy", icon: Rocket },
];

function StepRail({
  step,
  setStep,
  form,
}: {
  step: Step;
  setStep: (s: Step) => void;
  form: CreateAgentInput;
}) {
  const vm = VM_SIZES.find((v) => v.id === (form.runtimeConfig?.vm_size as string | undefined));
  const adapter = findAdapterCard(form.adapterType, form.adapterConfig?.runtimeMode);

  return (
    <aside
      className="hidden h-full w-[300px] min-w-[300px] shrink-0 flex-col border-r border-border bg-sidebar px-5 py-6 sm:flex"
    >
      <div className="mb-7 flex items-center gap-2.5">
        <Sparkles size={18} className="text-accent" />
        <span className="text-[15px] font-semibold">New agent</span>
      </div>

      <ol className="m-0 flex list-none flex-col gap-0.5 p-0">
        {STEPS.map((s, i) => {
          const done = s.id < step;
          const active = s.id === step;
          const clickable = s.id < step && step <= 5;
          const Icon = s.icon;
          return (
            <li key={s.id}>
              <button
                disabled={!clickable}
                onClick={() => clickable && setStep(s.id)}
                className={`relative flex w-full items-center gap-3 rounded-[10px] border-0 bg-transparent p-2.5 text-left ${
                  active ? "bg-accent/[0.08]" : ""
                } ${clickable ? "cursor-pointer hover:bg-surface-hover" : "cursor-default"}`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-semibold transition-all ${
                    done
                      ? "bg-accent text-white"
                      : active
                        ? "text-accent"
                        : "bg-surface-hover text-text-muted"
                  }`}
                  style={{ backgroundColor: active && !done ? "rgba(16,163,127,0.12)" : undefined }}
                >
                  {done ? <Check size={14} strokeWidth={2.5} /> : <Icon size={14} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className={`text-[13px] font-semibold ${
                      active ? "text-accent" : "text-text-primary"
                    }`}
                  >
                    {s.label}
                  </div>
                  <div className="text-[11.5px] text-text-muted">{s.desc}</div>
                </div>
              </button>
              {i < STEPS.length - 1 && (
                <div
                  className="ml-[22px] h-3 border-l-[1.5px]"
                  style={{
                    borderColor: done ? "var(--accent)" : "var(--border)",
                    opacity: done ? 0.4 : 1,
                  }}
                />
              )}
            </li>
          );
        })}
      </ol>

      {/* So-far summary */}
      <div className="mt-auto border-t border-border pt-5">
        <p className="mb-2.5 m-0 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
          So far
        </p>
        <div className="flex flex-col gap-1.5 text-[12.5px]">
          <SumRow k="Name" v={form.name || "—"} />
          <SumRow k="Role" v={form.title || form.role || "—"} />
          <SumRow k="Runtime" v={adapter?.label ?? "—"} />
          <SumRow k="Host" v={vm ? `${vm.label} · $${vm.cost}/mo` : "—"} />
        </div>
      </div>
    </aside>
  );
}

function SumRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-text-muted">{k}</span>
      <span className="max-w-[170px] overflow-hidden text-ellipsis whitespace-nowrap font-medium text-text-primary">
        {v}
      </span>
    </div>
  );
}

export function NewAgent({
  embedded = false,
  onClose,
  onCreated,
}: {
  embedded?: boolean;
  /** Embedded: leave the wizard without a route change (host closes it). */
  onClose?: () => void;
  /** Embedded: the created agent id, so the host can open it in place. */
  onCreated?: (agentId: string) => void;
} = {}) {
  useDocumentTitle("New agent");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  // Use the availability endpoint so Foundry (azure_foundry) models appear in
  // the picker with their access status, alongside the Anthropic catalogue.
  const { data: availability } = useQuery({
    queryKey: ["models", "availability", tenantId],
    queryFn: () => modelsApi.getAvailability(tenantId),
  });
  const catalogueModels = availability?.models ?? [];
  const { data: onboarding } = useQuery({
    queryKey: ["onboarding", tenantId],
    queryFn: () => onboardingApi.getStatus(tenantId),
  });
  const departments = onboarding?.departments ?? [];

  const [step, setStep] = useState<Step>(1);
  const [templateId, setTemplateId] = useState<string>("it-ops");
  const [createdAgent, setCreatedAgent] = useState<Agent | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<CreateAgentInput>({
    companyId: tenantId,
    name: "IT Operations",
    role: "ic",
    title: "IT Operations Specialist",
    department: "IT",
    reportsTo: null,
    adapterType: "openclaw_gateway",
    adapterConfig: { model: "claude-sonnet-4-5", gateway_port: 18789 },
    runtimeTier: "vm",
    runtimeConfig: {
      heartbeat_interval_sec: 300,
      timeout_sec: 600,
      max_retries: 3,
      vm_size: "Standard_B2s",
      region: "uksouth",
    },
    budgetMonthlyCents: 50000,
    capabilities: "Answer L1 tickets · Run diagnostics · Query logs · Restart services",
  });

  const set = (patch: Partial<CreateAgentInput>) =>
    setForm((f) => ({ ...f, ...patch }));

  const onSelectTemplate = (t: Template) => {
    setTemplateId(t.id);
    setForm((f) => ({
      ...f,
      name: t.id === "blank" ? f.name : t.name,
      role: t.role,
      department: t.department || null,
      adapterType: t.adapterType,
      capabilities: t.capabilities,
      runtimeConfig: {
        ...(f.runtimeConfig ?? {}),
        vm_size: t.vmSize,
      },
    }));
  };

  const createMutation = useMutation({
    mutationFn: () => agentsApi.create(form),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.deployments.byCompany(tenantId) });
      agentsApi.generateAvatar(agent.id).catch(() => {});
      setCreatedAgent(agent);
      setStep(7);
    },
    onError: (err: Error) => {
      setError(err.message ?? "Failed to create agent.");
    },
  });

  // Embedded (settings dialog) leaves via callbacks instead of routing, so the
  // wizard stays inside the dialog rather than navigating to a full page.
  const closeWizard = () => {
    if (embedded) onClose?.();
    else navigate(`/tenants/${tenantId}/agents`);
  };
  const openCreated = () => {
    if (!createdAgent) return;
    if (embedded) onCreated?.(createdAgent.id);
    else navigate(`/tenants/${tenantId}/agents/${createdAgent.id}?tab=config`);
  };

  const canProceed = useMemo(() => {
    if (step === 1) return !!templateId;
    if (step === 2) return form.name.trim().length > 0;
    return true;
  }, [step, templateId, form.name]);

  const goNext = () => {
    if (step < 6) setStep((step + 1) as Step);
    else if (step === 6) createMutation.mutate();
  };
  const goBack = () => {
    if (step > 1 && step <= 6) setStep((step - 1) as Step);
  };

  const showRail = step <= 6;

  return (
    <div className="flex h-full w-full bg-background">
      {showRail && <StepRail step={step} setStep={setStep} form={form} />}

      <main className="flex flex-1 flex-col overflow-hidden">
        {showRail && (
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-7">
            <div className="flex items-center gap-2.5 text-[13px] text-text-muted">
              <button
                onClick={closeWizard}
                className="border-0 bg-transparent text-text-muted hover:text-text-primary"
              >
                Agents
              </button>
              <ChevronRight size={12} />
              <span className="font-medium text-text-primary">New agent</span>
            </div>
            <button
              onClick={closeWizard}
              className="inline-flex items-center gap-1.5 border-0 bg-transparent text-xs text-text-muted hover:text-text-primary"
            >
              <X size={14} /> Close
            </button>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {showRail ? (
            <div className="mx-auto max-w-[820px] px-8 pt-9 pb-[120px]">
              {step === 1 && (
                <StepTemplate
                  templateId={templateId}
                  set={set}
                  onSelectTemplate={onSelectTemplate}
                />
              )}
              {step === 2 && (
                <StepIdentity
                  form={form}
                  set={set}
                  agents={agents}
                  departments={departments}
                />
              )}
              {step === 3 && (
                <StepWorkspace form={form} set={set} companyId={tenantId} />
              )}
              {step === 4 && (
                <StepBrain form={form} set={set} catalogueModels={catalogueModels} />
              )}
              {step === 5 && <StepInfra form={form} set={set} />}
              {step === 6 && (
                <StepReview
                  form={form}
                  agents={agents}
                  catalogueModels={catalogueModels}
                />
              )}

              {step === 6 && error && (
                <div className="mt-4 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
                  {error}
                </div>
              )}
            </div>
          ) : step === 7 ? (
            error ? (
              <ProvisioningError
                error={error}
                onRetry={() => {
                  setError(null);
                  setStep(6);
                }}
                onOpen={openCreated}
              />
            ) : (
              <Provisioning
                form={form}
                tenantId={tenantId}
                agentId={createdAgent?.id ?? null}
                onDone={() => setStep(8)}
                onFail={(msg) => setError(msg)}
                onCancel={() => (createdAgent ? openCreated() : setStep(6))}
              />
            )
          ) : (
            <AgentAlive
              form={form}
              onChat={() =>
                createdAgent &&
                (embedded
                  ? onCreated?.(createdAgent.id)
                  : navigate(`/tenants/${tenantId}/agents/${createdAgent.id}?tab=chat`))
              }
              onOpen={openCreated}
              onBack={closeWizard}
            />
          )}
        </div>

        {showRail && (
          <div className="sticky bottom-0 flex shrink-0 items-center justify-between border-t border-border bg-background px-8 py-3.5">
            <Button
              variant="ghost"
              onClick={goBack}
              className={step === 1 ? "invisible" : ""}
            >
              <ArrowLeft size={14} /> Back
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-text-muted">{step} of 6</span>
              {step < 6 ? (
                <Button data-tour="wizard-continue" onClick={goNext} disabled={!canProceed}>
                  Continue <ArrowRight size={14} />
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={goNext}
                  disabled={createMutation.isPending}
                >
                  <Rocket size={14} />
                  {createMutation.isPending ? "Deploying…" : "Deploy agent"}
                </Button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
